import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useBreakpoint,
  useGetUser,
  useOutsideClick,
  usePaymentControlActions,
  useQueryParams,
  useResetPaymentControl,
  useSubscriptionInfo,
  useSubscriptionPlans,
  useVisible,
} from 'src/hooks';
import {
  LEGACY_PLAN_KINDS,
  NINJA_SUPPORT_EMAIL,
  PAYPAL_PAYMENT_IN_PROGRESS,
  PAYPAL_PAYMENT_IN_PROGRESS_MESSAGE,
  PLAN_KINDS_WITH_FAMILY_MAP,
  REDIRECT_TO_STRIPE_TIMESTAMP,
  SUBSCRIPTION_PENDING_CHANGES_NOTIFICATION_MESSAGE,
} from 'src/constants';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, CanonicalInterval, PlanFamily, PlanKind } from 'src/types';
import styles from './PaymentBetaPage.module.scss';
import { LoadingOverlay } from 'src/pages/PaymentBetaPage/components/LoadingOverlay';
import { isAppleOrAmpSource } from 'src/utils';
import { getWithExpiry } from 'src/utils/localStorage';
import { toast } from 'react-toastify';
import { PlanCardMaxPromoTooltip } from './components/PlanCardMaxPromoTooltip';
import { PlanGroup } from 'src/types/wallet/models/PlanGroup';
import { RequestUsageTable } from './components/RequestUsage/components/RequestUsageTable';
import { PaymentContainer } from 'src/pages/PaymentBetaPage/componentsV2/PaymentContainer';
import { PaymentGoBackButton } from 'src/pages/PaymentBetaPage/componentsV2/PaymentGoBackButton';
import { CheckoutSection } from 'src/pages/PaymentBetaPage/componentsV2/CheckoutSection';
import { UpgradeDowngradeSection } from 'src/pages/PaymentBetaPage/componentsV2/UpgradeDowngradeSection';

export const PaymentPage = () => {
  useResetPaymentControl();

  const navigate = useNavigate();
  const { searchParams } = useQueryParams();
  const { selected_plan, timestamp } = searchParams;
  const { isMobileOrTablet } = useBreakpoint();

  const { user_id, isOpenTier } = useGetUser();

  const { onSelectComplexPaymentPlan, onSetSelectedPlan } =
    usePaymentControlActions();

  const [isPaymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [appliedCoupon, setAppliedCoupon] = useState<string | undefined>(
    undefined,
  );

  const { subscriptionInfo, isSubscriptionInfoLoading } = useSubscriptionInfo();

  const { isPlansLoadingFirstTime } = useSubscriptionPlans({});

  const isCommonLoading =
    isPlansLoadingFirstTime || isPaymentProcessing || isSubscriptionInfoLoading;

  const {
    isVisible: isEmbeddedCheckoutVisible,
    onVisibilitySet: onOpenEmbeddedCheckoutVisible,
    onVisibilityRemove: onCloseEmbeddedCheckoutVisible,
  } = useVisible();

  const {
    isVisible: isMaxTooltipVisible,
    onVisibilitySet: onMaxTooltipVisibilitySet,
    onVisibilityRemove: onMaxTooltipVisibilityRemove,
  } = useVisible();

  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onMaxTooltipVisibilitySet();

    const hideTimer = setTimeout(() => {
      onMaxTooltipVisibilityRemove();
    }, 5000);

    return () => {
      onMaxTooltipVisibilityRemove();
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOutsideClick = () => {
    if (!isMaxTooltipVisible || isMobileOrTablet) {
      return;
    }

    onMaxTooltipVisibilityRemove();
  };

  useOutsideClick(tooltipRef, handleOutsideClick);

  const handleOpenCheckout = async () => {
    onOpenEmbeddedCheckoutVisible();
  };

  const isCurrentTierTeams =
    subscriptionInfo?.plan_family === PlanFamily.BUSINESS ||
    subscriptionInfo?.plan_group === PlanGroup.TEAMS;

  const isSubscribedUser = useMemo(
    () => !!subscriptionInfo && !isSubscriptionInfoLoading,
    [subscriptionInfo, isSubscriptionInfoLoading],
  );

  const isShowUpdateToAnnually = useMemo(
    () =>
      subscriptionInfo?.interval === CanonicalInterval.MONTHLY &&
      !isSubscriptionInfoLoading,
    [subscriptionInfo, isSubscriptionInfoLoading],
  );

  const handleCloseCheckout = () => {
    onCloseEmbeddedCheckoutVisible();
    onSetSelectedPlan({ planKind: undefined });
  };

  useEffect(() => {
    if (
      subscriptionInfo &&
      (subscriptionInfo.is_cancelled ||
        subscriptionInfo.has_scheduled_downgrade ||
        subscriptionInfo.has_scheduled_plan_change ||
        subscriptionInfo.processing_plan_change ||
        isAppleOrAmpSource(subscriptionInfo.source))
    ) {
      navigate(AppRoutes.HOME, { replace: true });
      toast.error(
        !isAppleOrAmpSource(subscriptionInfo.source) ? (
          SUBSCRIPTION_PENDING_CHANGES_NOTIFICATION_MESSAGE
        ) : (
          <span>
            Contact{' '}
            <a className={styles.link} href={`mailto:${NINJA_SUPPORT_EMAIL}`}>
              {NINJA_SUPPORT_EMAIL}
            </a>{' '}
            as your original subscription is made through Amazon Partner Network
            or Mobile app
          </span>
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionInfo]);

  useEffect(() => {
    if (user_id && isOpenTier) {
      localStorage.setItem(
        REDIRECT_TO_STRIPE_TIMESTAMP,
        dayjs().valueOf().toString(),
      );
      navigate(AppRoutes.LOGIN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenTier, user_id]);

  useEffect(() => {
    if (selected_plan) {
      const selectedPlan = selected_plan as PlanKind;
      const selectedPlanFamily = PLAN_KINDS_WITH_FAMILY_MAP[selectedPlan];
      const isSelectedCurrentPlan =
        selectedPlan === subscriptionInfo?.plan_kind;
      const isDisabledDowngrade =
        (subscriptionInfo?.plan_family === PlanFamily.BUSINESS ||
          subscriptionInfo?.plan_group === PlanGroup.TEAMS) &&
        selectedPlanFamily !== PlanFamily.BUSINESS;
      const selectedPeriod = selectedPlan.split('-').pop() as CanonicalInterval;
      const isWrongPeriod =
        !selectedPeriod ||
        (selectedPeriod === CanonicalInterval.MONTHLY &&
          subscriptionInfo?.interval === CanonicalInterval.ANNUALLY);

      if (
        !isCommonLoading &&
        !!selectedPlanFamily &&
        selectedPlanFamily !== PlanFamily.UNDEFINED &&
        !LEGACY_PLAN_KINDS.includes(selectedPlan)
      ) {
        if (!isSelectedCurrentPlan && !isDisabledDowngrade && !isWrongPeriod) {
          onSelectComplexPaymentPlan({
            planFamily: selectedPlanFamily,
            planKind: selectedPlan,
            period: selectedPeriod,
          });
          onSetSelectedPlan({ planKind: selectedPlan });
          onOpenEmbeddedCheckoutVisible();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected_plan, isCommonLoading, subscriptionInfo, timestamp]);

  useEffect(() => {
    const paypalPaymentInProgress =
      getWithExpiry(PAYPAL_PAYMENT_IN_PROGRESS) === 'true';

    if (paypalPaymentInProgress) {
      toast.info(
        `${PAYPAL_PAYMENT_IN_PROGRESS_MESSAGE} Please wait until it's completed.`,
      );
      navigate(AppRoutes.HOME);
    }
  }, [navigate]);

  return (
    <div className={styles.root}>
      {isCommonLoading && (
        <LoadingOverlay
          label={
            isPaymentProcessing ? 'Processing payment...' : 'Loading plans...'
          }
        />
      )}

      <PaymentGoBackButton />

      {!isCurrentTierTeams && (
        <div ref={tooltipRef}>
          <PlanCardMaxPromoTooltip isOpen={isMaxTooltipVisible} />
        </div>
      )}

      <PaymentContainer
        onOpenCheckout={handleOpenCheckout}
        onCloseCheckout={handleCloseCheckout}
        isShowUpdateToAnnually={isShowUpdateToAnnually}
        onChangeAppliedCoupon={setAppliedCoupon}
      />

      {isSubscribedUser ? (
        <UpgradeDowngradeSection
          isOpen={isEmbeddedCheckoutVisible}
          onClose={handleCloseCheckout}
        />
      ) : (
        <CheckoutSection
          isOpen={isEmbeddedCheckoutVisible}
          onClose={handleCloseCheckout}
          onPaymentProcessing={setPaymentProcessing}
          appliedCoupon={appliedCoupon}
          onChangeAppliedCoupon={setAppliedCoupon}
        />
      )}

      <RequestUsageTable isNewVariant={true} />
    </div>
  );
};
